import {
  Activity,
  Aperture,
  ArrowLeftRight,
  Brush,
  ChartLine,
  ClipboardCheck,
  Columns3,
  Crosshair,
  Database,
  Download,
  Gauge,
  Globe,
  Grid2x2Check,
  Layers,
  ListChecks,
  ListTodo,
  Lock,
  Network,
  PackageSearch,
  ScanBarcode,
  ScanSearch,
  SlidersHorizontal,
  SlidersVertical,
  TriangleAlert,
} from 'lucide-react';
import React from 'react';
import { TitleEntry } from '../../../navigation/titleEntry';
import {
  FORGE_CLEANING_TASK_LIST_PATH,
  FORGE_CLEANING_TOOL_PATH,
  FORGE_COVERAGE_PATH,
  FORGE_DESIGNS_DASHBOARD_PATH,
  FORGE_DESIGN_GROUPS_DASHBOARD_PATH,
  FORGE_DISTRACTORS_PATH,
  FORGE_EXPORT_PATH,
  FORGE_ITEM_SEARCH_PATH,
  FORGE_LOCKED_DESIGNS_DASHBOARD_PATH,
  FORGE_PR_INFERENCE_OKRS_PATH,
  FORGE_PR_REVIEW_PATH,
  FORGE_RECOGNITION_OKRS_PATH,
  FORGE_RECOGNITION_REPLAY_PATH,
  FORGE_RECOGNITION_THRESHOLDS_CONFIG_PATH,
  FORGE_REMOTE_DC_CONFIG_PATH,
  FORGE_REMOTE_DC_TASK_LIST_PATH,
  FORGE_REMOTE_DC_TOOL_PATH,
  FORGE_REPORTED_ISSUES_PATH,
  FORGE_SCAN_ASSIGNMENT_PATH,
  FORGE_SCAN_THRESHOLDS_CONFIG_PATH,
  FORGE_STORE_DC_TASK_LIST_PATH,
  FORGE_SYSTEM_HEALTH_PATH,
  FORGE_TARGETED_DC_CONFIG_PATH,
  FORGE_VIZSIM_DATASETS_DASHBOARD_PATH,
} from '../../paths';

/**
 * The one list of Forge destinations. The sidebar renders it, the mobile topbar
 * takes its label from it, and the document title is built from it, so a new
 * route is named once.
 *
 * A label names its destination on its own: "Remote DC Tool" rather than
 * "Remote DC" under a "Labeling Tools" heading. The section is grouping, not
 * disambiguation, so the label survives everywhere the section does not — the
 * browser tab, the mobile topbar, and the collapsed sidebar's tooltip. That is
 * why there is no separate tab label any more (DLI-1907).
 *
 * Sections group by area — the thing you are working on — rather than by the
 * kind of page, because the kind is now in the label. Each area's queue, tool
 * and configuration sit together, which also lines the sections up with the
 * permission gates in `ForgeSidebar`: a Recognition reviewer sees one
 * Recognition section, a Remote DC labeler sees one Remote DC section.
 */

export interface ForgeNavItem {
  label: string;
  icon: React.ReactNode;
  path: string;
}

export interface ForgeNavSection {
  title: string;
  items: ForgeNavItem[];
}

export const FORGE_NAV_SECTIONS: ForgeNavSection[] = [
  {
    title: 'Monitoring',
    items: [
      {
        label: 'System Health',
        icon: <Activity />,
        path: FORGE_SYSTEM_HEALTH_PATH,
      },
      {
        label: 'Reported Issues',
        icon: <TriangleAlert />,
        path: FORGE_REPORTED_ISSUES_PATH,
      },
      {
        label: 'Embedding Coverage',
        icon: <Grid2x2Check />,
        path: FORGE_COVERAGE_PATH,
      },
    ],
  },
  {
    title: 'Catalog',
    items: [
      {
        label: 'Designs',
        icon: <Layers />,
        path: FORGE_DESIGNS_DASHBOARD_PATH,
      },
      {
        label: 'Design Groups',
        icon: <Network />,
        path: FORGE_DESIGN_GROUPS_DASHBOARD_PATH,
      },
      {
        label: 'Locked Designs',
        icon: <Lock />,
        path: FORGE_LOCKED_DESIGNS_DASHBOARD_PATH,
      },
      {
        label: 'Export',
        icon: <Download />,
        path: FORGE_EXPORT_PATH,
      },
    ],
  },
  {
    title: 'Recognition',
    items: [
      {
        label: 'PR Inference OKRs',
        icon: <ChartLine />,
        path: FORGE_PR_INFERENCE_OKRS_PATH,
      },
      {
        label: 'Recognition OKRs',
        icon: <ClipboardCheck />,
        path: FORGE_RECOGNITION_OKRS_PATH,
      },
      {
        label: 'Recognition Replay',
        icon: <ArrowLeftRight />,
        path: FORGE_RECOGNITION_REPLAY_PATH,
      },
      {
        label: 'Recognition Review',
        icon: <ListChecks />,
        path: FORGE_PR_REVIEW_PATH,
      },
      {
        label: 'VizSim Datasets',
        icon: <Database />,
        path: FORGE_VIZSIM_DATASETS_DASHBOARD_PATH,
      },
      {
        label: 'Recognition Thresholds',
        icon: <Gauge />,
        path: FORGE_RECOGNITION_THRESHOLDS_CONFIG_PATH,
      },
    ],
  },
  {
    title: 'Remote DC',
    items: [
      {
        label: 'Remote DC Tasks',
        icon: <ListTodo />,
        path: FORGE_REMOTE_DC_TASK_LIST_PATH,
      },
      {
        label: 'Remote DC Tool',
        icon: <Globe />,
        path: FORGE_REMOTE_DC_TOOL_PATH,
      },
      {
        label: 'Distractors',
        icon: <Aperture />,
        path: FORGE_DISTRACTORS_PATH,
      },
      {
        label: 'Remote DC Config',
        icon: <SlidersHorizontal />,
        path: FORGE_REMOTE_DC_CONFIG_PATH,
      },
    ],
  },
  {
    title: 'Cleaning',
    items: [
      {
        label: 'Cleaning Tasks',
        icon: <Brush />,
        path: FORGE_CLEANING_TASK_LIST_PATH,
      },
      {
        label: 'Cleaning Tool',
        icon: <Columns3 />,
        path: FORGE_CLEANING_TOOL_PATH,
      },
      {
        label: 'Item Search',
        icon: <PackageSearch />,
        path: FORGE_ITEM_SEARCH_PATH,
      },
    ],
  },
  {
    title: 'Store DC',
    items: [
      {
        label: 'Store DC',
        icon: <ScanSearch />,
        path: FORGE_STORE_DC_TASK_LIST_PATH,
      },
      {
        label: 'Scan Assignment',
        icon: <ScanBarcode />,
        path: FORGE_SCAN_ASSIGNMENT_PATH,
      },
      {
        label: 'Scan Thresholds',
        icon: <SlidersVertical />,
        path: FORGE_SCAN_THRESHOLDS_CONFIG_PATH,
      },
      {
        label: 'Targeted DC Config',
        icon: <Crosshair />,
        path: FORGE_TARGETED_DC_CONFIG_PATH,
      },
    ],
  },
];

export const FORGE_APP_TITLE = 'Forge';

const NAV_MATCHES: { path: string; label: string }[] = FORGE_NAV_SECTIONS.flatMap((section) =>
  section.items.map((item) => ({ path: item.path, label: item.label }))
);

/** The short name for the mobile topbar. */
export function getForgePageLabel(pathname: string): string {
  // Longest path wins, so a nested route resolves to its own entry rather than a
  // shorter prefix that happens to share the first segments.
  return (
    NAV_MATCHES.filter(({ path }) => pathname === path || pathname.startsWith(`${path}/`)).sort(
      (left, right) => right.path.length - left.path.length
    )[0]?.label ?? FORGE_APP_TITLE
  );
}

/** Browser tab names for Forge, taken from the destinations the sidebar renders. */
export const FORGE_TITLE_ENTRIES: TitleEntry[] = FORGE_NAV_SECTIONS.flatMap((section) =>
  section.items.map((item) => ({ path: item.path, label: item.label }))
);
